import { addDays, differenceInCalendarDays, format, parse, startOfDay, subDays } from 'date-fns';
import { chain, find, orderBy } from 'lodash';
import { useMemo } from 'react';

import { useSatelliteCreditsUsageHistory } from '@/api/credits/getSatelliteCreditsUsage';
import { SatelliteCreditsUsageItem } from '@/api/credits/types';
import { useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { useCreditBalance } from '@/api/userAccount';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';

export const DEFAULT_CREDIT_USAGE_HISTORY_DAYS = 30;

type UseSatelliteCreditUsageStatsOptions = {
    config?: { enabled?: boolean };
    /**
     * Number of days of history to consider (defaults to {@link DEFAULT_CREDIT_USAGE_HISTORY_DAYS})
     */
    historyDays?: number;
    /**
     * Number of credits used to calculate run-out estimate. If not specified, uses customer's current credit balance.
     *
     * This does not impact the historical burn down, which always works backward from the current credit balance.
     */
    creditsForRunOutEstimate?: number;
};

export type SatelliteCreditUsageStats = {
    historyDays: number;
    totalUsageAmount: number;
    runOutOfCreditsEstimate: Date | null;
    averageDailyUsageByActiveSpotters: number;
    usageHistory: SatelliteCreditsUsageItem[];
    balanceOnDate: { [yyymmdd: string]: number };
    spotterUsage: {
        [spotterId: string]: {
            isActive: boolean;
            usageAmount: number;
            usageDays: number;
        };
    };
};

export type UseSatelliteCreditUsageStatsResult =
    | {
          data: undefined;
          isPending: true;
          isError: false;
      }
    | {
          data: undefined;
          isPending: false;
          isError: true;
      }
    | {
          data: SatelliteCreditUsageStats;
          isPending: false;
          isError: false;
      };

export const useSatelliteCreditUsageStats = ({
    config: { enabled = true } = {},
    historyDays = DEFAULT_CREDIT_USAGE_HISTORY_DAYS,
    creditsForRunOutEstimate,
}: UseSatelliteCreditUsageStatsOptions = {}): UseSatelliteCreditUsageStatsResult => {
    const now = useNow(3600);
    const tzContext = useTzContext();
    const from = format(subDays(now, historyDays), 'yyyy-MM-dd');

    const {
        data: usageHistory,
        isPending: usageHistoryPending,
        isError: usageHistoryError,
    } = useSatelliteCreditsUsageHistory({
        config: { enabled },
        from,
    });
    const { data: balance, isPending: isBalancePending } = useCreditBalance({ config: { enabled } });
    const {
        data: subsTags,
        isPending: subsTagsPending,
        isError: subsTagsError,
    } = useSubscriptionTagsInfo({ config: { enabled } });

    return useMemo(() => {
        if (usageHistoryPending || subsTagsPending || isBalancePending) {
            return { data: undefined, isPending: true, isError: false };
        }
        if (usageHistoryError || subsTagsError) {
            return { data: undefined, isPending: false, isError: true };
        }

        const roundTransactionTs = (transaction_date: string) =>
            parse(transaction_date, 'yyyy-MM-dd', startOfDay(now, tzContext), tzContext);

        let totalUsageAmount = 0;
        let runningBalance = balance?.data.credit_balance ?? 0;
        const balanceOnDate: SatelliteCreditUsageStats['balanceOnDate'] = {};
        const spotterUsage: SatelliteCreditUsageStats['spotterUsage'] = {};
        // This is the same order the API returns it in, but since there are only 30 elements and the algorithm
        // relies on reverse chronological order, might as well be defensive.
        const sortedUsage = orderBy(usageHistory.data, 'transaction_date', 'desc');
        const spotterEarliestContinuousUsageDay: { [spotterId: string]: Date } = {};
        let firstDay = true;
        sortedUsage.forEach(({ consumption_breakdown, transaction_date, total_refills, total_consumptions }) => {
            totalUsageAmount += total_consumptions;
            runningBalance = runningBalance - total_refills + total_consumptions;
            balanceOnDate[transaction_date] = runningBalance;
            const transactionTs = roundTransactionTs(transaction_date);
            consumption_breakdown.forEach(({ spotter_id, amount }) => {
                if (!amount) {
                    return;
                }
                if (firstDay) {
                    spotterEarliestContinuousUsageDay[spotter_id] = transactionTs;
                } else if (
                    spotterEarliestContinuousUsageDay[spotter_id] &&
                    differenceInCalendarDays(
                        spotterEarliestContinuousUsageDay[spotter_id],
                        transactionTs,
                        tzContext
                    ) === 1
                ) {
                    spotterEarliestContinuousUsageDay[spotter_id] = transactionTs;
                }
            });
            firstDay = false;
        });
        // now that we know how long each spotter has been continuously active, we can figure out how far back we actually want to calculate each spotter's
        // usage in a second pass.
        sortedUsage.forEach(({ consumption_breakdown, transaction_date }) => {
            consumption_breakdown.forEach(({ spotter_id, amount }) => {
                const transactionTs = roundTransactionTs(transaction_date);
                if (!spotterUsage[spotter_id]) {
                    const tags = find(subsTags.data, { spotter_id });
                    const isActive = (tags?.should_iridium_be_active && tags?.is_iridium_allowed) ?? false;
                    const daysSinceActivated =
                        differenceInCalendarDays(now, tags?.should_iridium_be_active_last_modified ?? 0, tzContext) + 1;
                    const daysOfContinuousUsage = spotterEarliestContinuousUsageDay[spotter_id]
                        ? differenceInCalendarDays(now, spotterEarliestContinuousUsageDay[spotter_id], tzContext)
                        : 0;
                    const usageDays = isActive
                        ? Math.min(Math.max(daysSinceActivated, daysOfContinuousUsage), historyDays)
                        : historyDays;
                    spotterUsage[spotter_id] = { usageAmount: 0, isActive, usageDays };
                }
                if (differenceInCalendarDays(now, transactionTs, tzContext) <= spotterUsage[spotter_id].usageDays) {
                    spotterUsage[spotter_id].usageAmount += amount;
                }
            });
        });

        const averageDailyUsageByActiveSpotters = chain(spotterUsage)
            .values()
            .filter('isActive')
            .map(({ usageAmount, usageDays }) => usageAmount / usageDays)
            .sum()
            .value();

        const creditBalance = creditsForRunOutEstimate ?? balance?.data.credit_balance;
        const runOutOfCreditsEstimate =
            averageDailyUsageByActiveSpotters && creditBalance
                ? startOfDay(
                      // zeroing out time just so it doesn't look like we're predicting an exact moment of exhaustion
                      addDays(now, Math.floor(creditBalance / averageDailyUsageByActiveSpotters)),
                      tzContext
                  )
                : null;

        const data: SatelliteCreditUsageStats = {
            totalUsageAmount,
            spotterUsage,
            historyDays,
            runOutOfCreditsEstimate,
            averageDailyUsageByActiveSpotters,
            usageHistory: usageHistory.data,
            balanceOnDate,
        };

        return { data, isPending: false, isError: false };
    }, [
        usageHistoryPending,
        subsTagsPending,
        isBalancePending,
        usageHistoryError,
        creditsForRunOutEstimate,
        subsTagsError,
        balance?.data.credit_balance,
        usageHistory?.data,
        now,
        tzContext,
        historyDays,
        subsTags?.data,
    ]);
};
